import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { Brain, Pin, MessageSquare, Sparkles, TrendingUp, Users, Zap } from 'lucide-react'
import { Responsive, WidthProvider, type Layout } from 'react-grid-layout'
import { usePinnedCardsContext } from '@/contexts/PinnedCardsContext'
import { PinnedCard } from '@/components/intelligence/PinnedCard'
import { useEventData } from '@/contexts/EventDataContext'
import { useAuth } from '@/contexts/AuthContext'
import { useChat } from '@/contexts/ChatContext'

import 'react-grid-layout/css/styles.css'
import 'react-resizable/css/styles.css'

const ResponsiveGridLayout = WidthProvider(Responsive)

const EXAMPLE_PROMPTS = [
  'What was our peak revenue day at the last event?',
  'Compare ride revenue vs game revenue this season',
  'Which employees generated the most scans per hour?',
  'Show me weather impact on our events',
]

export function IntelligencePage() {
  const { cards, unpinCard, updateLayouts } = usePinnedCardsContext()
  const { stats, loading: dataLoading } = useEventData()
  const { currentEventId, events } = useAuth()
  const activeEvent = events.find((e) => e.EventID === currentEventId)

  const layouts = useMemo(
    () => ({
      lg: cards.map((c) => ({ ...c.layout, i: c.id })),
      md: cards.map((c) => ({ ...c.layout, i: c.id, w: Math.min(c.layout.w, 6) })),
      sm: cards.map((c) => ({ ...c.layout, i: c.id, x: 0, w: 12 })),
    }),
    [cards]
  )

  const handleLayoutChange = (_current: Layout[], allLayouts: { [key: string]: Layout[] }) => {
    const lgLayouts = allLayouts.lg
    if (lgLayouts) {
      updateLayouts(lgLayouts)
    }
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-butler-50">
            <Brain className="h-5 w-5 text-butler-600" />
          </div>
          <div>
            <h1 className="font-display font-bold text-xl text-ink-dark">Intelligence</h1>
            <p className="text-sm text-ink-soft">
              Your custom dashboard — pin answers from chat to build it
            </p>
          </div>
        </div>
        {cards.length > 0 && (
          <div className="flex items-center gap-1.5 text-xs text-ink-soft">
            <Pin className="h-3.5 w-3.5" />
            <span>{cards.length} pinned</span>
          </div>
        )}
      </div>

      {activeEvent && stats?.metrics && (
        <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
          <div className="mm-card-v3 p-4">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-ink-soft mb-2">
              <TrendingUp className="h-3.5 w-3.5" /> Revenue
            </div>
            <div className="font-display text-2xl font-bold text-ink-dark">
              ${(stats.metrics.revenue_total / 100).toLocaleString(undefined, { maximumFractionDigits: 0 })}
            </div>
          </div>
          <div className="mm-card-v3 p-4">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-ink-soft mb-2">
              <Users className="h-3.5 w-3.5" /> Guests
            </div>
            <div className="font-display text-2xl font-bold text-ink-dark">
              {stats.metrics.guests.toLocaleString()}
            </div>
          </div>
          <div className="mm-card-v3 p-4">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-ink-soft mb-2">
              <Zap className="h-3.5 w-3.5" /> Riders / hr
            </div>
            <div className="font-display text-2xl font-bold text-ink-dark">
              {stats.metrics.riders_per_hour.toLocaleString()}
            </div>
          </div>
        </div>
      )}
      {dataLoading && !stats && (
        <div className="mb-6 text-sm text-ink-soft animate-pulse">Loading event intelligence...</div>
      )}

      {cards.length === 0 ? (
        <EmptyState />
      ) : (
        <div role="region" aria-label="Pinned Intelligence board — drag cards to rearrange">
          <ResponsiveGridLayout
            className="layout"
            layouts={layouts}
            breakpoints={{ lg: 1200, md: 996, sm: 0 }}
            cols={{ lg: 12, md: 6, sm: 12 }}
            rowHeight={60}
            isDraggable
            isResizable
            draggableHandle=".drag-handle"
            onLayoutChange={handleLayoutChange}
          >
            {cards.map((card) => (
              <div
                key={card.id}
                className="drag-handle cursor-grab active:cursor-grabbing"
                role="article"
                aria-label={`Pinned: ${card.question.slice(0, 80)}`}
              >
                <PinnedCard
                  question={card.question}
                  answer={card.answer}
                  pinnedAt={card.pinnedAt}
                  onUnpin={() => unpinCard(card.id)}
                />
              </div>
            ))}
          </ResponsiveGridLayout>
        </div>
      )}
    </div>
  )
}

function EmptyState() {
  const { setPendingMessage } = useChat()
  const navigate = useNavigate()

  const askQuestion = (prompt: string) => {
    setPendingMessage(prompt)
    navigate('/home')
  }

  return (
    <div className="flex flex-col items-center justify-center py-20 px-6">
      <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-butler-50 mb-5">
        <Sparkles className="h-8 w-8 text-butler-500" />
      </div>
      <h2 className="font-display font-semibold text-lg text-ink-dark mb-2">
        Build your Intelligence board
      </h2>
      <p className="text-sm text-ink-soft text-center max-w-md mb-8">
        Ask the agent a question in chat, then tap the{' '}
        <Pin className="inline h-3.5 w-3.5 text-butler-500 align-text-bottom" /> pin icon on any
        answer to add it here. Cards are draggable and resizable.
      </p>

      <div className="w-full max-w-lg">
        <div className="flex items-center gap-2 mb-3">
          <MessageSquare className="h-4 w-4 text-ink-soft" />
          <span className="text-xs font-medium text-ink-soft uppercase tracking-wide">
            Try asking
          </span>
        </div>
        <div className="grid gap-2">
          {EXAMPLE_PROMPTS.map((prompt) => (
            <button
              key={prompt}
              type="button"
              onClick={() => askQuestion(prompt)}
              className="mm-card-v3 px-4 py-3 text-sm text-ink-dark text-left hover:border-butler-300 hover:bg-butler-50/50 transition-colors cursor-pointer"
            >
              <MessageSquare className="inline h-3.5 w-3.5 text-butler-500 mr-2" />
              {prompt}
            </button>
          ))}
        </div>
      </div>
    </div>
  )
}
